import { toDayKey } from "@/app/utils/calendarDay";
import { activityStartInstant } from "@/app/utils/centerTime";

/**
 * Minimum hours between the moment of booking and the activity start, so
 * dive centers have time to review requests. Default is 36h; each listing
 * can override it via Listing.minBookingLeadHours.
 *
 * "10:00" is the dive center's wall clock, so every check that needs a real
 * moment takes the center's `timeZone`. Without one the old behaviour is kept:
 * the clock of whoever is running the code.
 */
export const DEFAULT_BOOKING_LEAD_HOURS = 36;

/** Kept for backwards compatibility with earlier imports. */
export const MIN_BOOKING_LEAD_HOURS = DEFAULT_BOOKING_LEAD_HOURS;

function resolveLeadHours(leadHours?: number | null): number {
  return typeof leadHours === "number" && leadHours >= 0
    ? leadHours
    : DEFAULT_BOOKING_LEAD_HOURS;
}

function parseStartTime(startTime?: string | null): {
  hours: number;
  minutes: number;
} | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(startTime || "");
  if (!match) return null;
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}

/** Activity start moment for a given calendar day, at the dive center. */
function activityStartFor(
  day: Date,
  startTime?: string | null,
  timeZone?: string | null
): Date {
  if (timeZone) return activityStartInstant(toDayKey(day), startTime, timeZone);
  const parsed = parseStartTime(startTime);
  const moment = new Date(day);
  moment.setHours(parsed ? parsed.hours : 0, parsed ? parsed.minutes : 0, 0, 0);
  return moment;
}

/**
 * First calendar day (at local midnight) whose activity start time is at
 * least `leadHours` away from now. When the listing has no startTime, the
 * first bookable day is simply the day `leadHours` from now.
 */
export function getMinBookableDate(
  startTime?: string | null,
  leadHours?: number | null,
  timeZone?: string | null
): Date {
  const lead = resolveLeadHours(leadHours);
  const cutoff = new Date(Date.now() + lead * 60 * 60 * 1000);

  if (!parseStartTime(startTime)) {
    // No startTime: assume the activity starts at midnight (same as
    // hoursLeftToBook), so round the cutoff UP to the next day boundary.
    const day = new Date(cutoff);
    day.setHours(0, 0, 0, 0);
    if (day.getTime() < cutoff.getTime()) day.setDate(day.getDate() + 1);
    return day;
  }

  const candidate = new Date();
  candidate.setHours(0, 0, 0, 0);

  for (let i = 0; i < 100; i++) {
    if (activityStartFor(candidate, startTime, timeZone) >= cutoff) return candidate;
    candidate.setDate(candidate.getDate() + 1);
  }
  return candidate;
}

/** Whether a given activity date satisfies the minimum lead time. */
export function isDateBookable(
  date: Date,
  startTime?: string | null,
  leadHours?: number | null,
  timeZone?: string | null
): boolean {
  if (timeZone) {
    const start = activityStartFor(date, startTime, timeZone);
    return start.getTime() - Date.now() >= resolveLeadHours(leadHours) * 3600000;
  }
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized >= getMinBookableDate(startTime, leadHours);
}

/**
 * Whether an exact activity start moment (e.g. a liveaboard trip departure
 * date) satisfies the minimum lead time.
 */
export function isStartMomentBookable(
  start: Date,
  leadHours?: number | null
): boolean {
  return start.getTime() - Date.now() >= resolveLeadHours(leadHours) * 60 * 60 * 1000;
}

/**
 * Hours remaining until booking closes for a given activity day, or null
 * when the deadline is more than 48h away (no urgency to show) or already
 * passed. Used for the "N hours left to book" badge.
 */
export function hoursLeftToBook(
  date: Date,
  startTime?: string | null,
  leadHours?: number | null,
  timeZone?: string | null
): number | null {
  const lead = resolveLeadHours(leadHours);
  const start = activityStartFor(new Date(date), startTime, timeZone);
  const deadline = start.getTime() - lead * 60 * 60 * 1000;
  const left = (deadline - Date.now()) / (60 * 60 * 1000);
  if (left <= 0 || left > 48) return null;
  return Math.max(1, Math.floor(left));
}
